import { motion } from "framer-motion";
import { ArrowRight, TrendingUp } from "lucide-react";
import { ShareButtons } from "@/components/ShareButtons";

const stories = [
  {
    name: "From Finance Manager to CFO",
    from: "Financial Manager",
    to: "Chief Financial Officer",
    field: "Healthcare · Pacific Northwest",
    story:
      "A finance manager at a regional healthcare organization came to coaching wanting the executive seat. Through hands-on mentorship — technical mastery, executive presence, and strategic finance leadership — they stepped into the CFO role within a few years.",
  },
];

export default function SuccessStories() {
  return (
    <section id="success-stories" data-testid="success-stories-section" className="border-t border-linen bg-white/40">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
        >
          Coaching Success Stories
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          className="mb-20 max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl"
          data-testid="success-stories-heading"
        >
          Careers that <span className="italic text-forest">outgrew</span> their starting point.
        </motion.h2>

        {stories.map((s, i) => (
          <motion.article
            key={s.name}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="grid grid-cols-1 gap-12 border border-linen bg-white p-10 md:grid-cols-12 md:p-14"
            data-testid={`success-story-${i}`}
          >
            <div className="md:col-span-5">
              <p className="mb-2 font-serif text-3xl font-semibold tracking-tight text-pine md:text-4xl" data-testid={`success-story-name-${i}`}>
                {s.name}
              </p>
              <p className="mb-10 font-mono text-[10px] uppercase tracking-[0.25em] text-forest">{s.field}</p>
              <div className="flex items-center gap-4" data-testid={`success-story-arc-${i}`}>
                <div className="border border-linen bg-bone px-5 py-4">
                  <p className="mb-1 font-mono text-[9px] uppercase tracking-[0.25em] text-pine/50">Started as</p>
                  <p className="font-serif text-lg font-semibold text-pine">{s.from}</p>
                </div>
                <ArrowRight size={20} className="shrink-0 text-terra" />
                <div className="border border-forest bg-forest px-5 py-4">
                  <p className="mb-1 font-mono text-[9px] uppercase tracking-[0.25em] text-bone/60">Became</p>
                  <p className="font-serif text-lg font-semibold text-bone">{s.to}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-between md:col-span-7">
              <p className="mb-8 font-serif text-xl italic leading-relaxed text-pine/85 md:text-2xl" data-testid={`success-story-text-${i}`}>
                {s.story}
              </p>
              <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-terra">
                <TrendingUp size={14} /> Mentored &amp; coached by Zecharias A. Mesgane
              </p>
            </div>
          </motion.article>
        ))}

        <div className="mt-14 flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <motion.a
            href="#booking"
            data-testid="success-stories-cta"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            whileTap={{ scale: 0.96 }}
            className="inline-flex items-center gap-2 self-start border border-forest bg-forest px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-forest"
          >
            Write your own story <ArrowRight size={16} />
          </motion.a>
          <ShareButtons label="Inspired? Share this story" />
        </div>
      </div>
    </section>
  );
}
